import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import { useProfilesData } from '@/features/user/api';

import type { ChatMessage, ChatResponse } from '../api/chat';
import { getChatHistory } from '../api/chat-history';
import type { BhanzuBuddyChatMessage } from '../components/bhanzu-buddy-chat/bhanzu-buddy-chat-types';
import { API_CONSTANTS } from '../constants';

import { useApiInteractions } from './utils/api-interactions';
import { getErrorMessage } from './utils/error-handling';
import { useGuideFlow } from './utils/guide-flow';
import { transformInteractionToMessages } from './utils/message-transformation';

export interface UseBhanzuBuddyChatProps {
  leadId: string;
  source?: string;
  onMessageSent?: (response: ChatResponse) => void;
  onError?: (error: unknown) => void;
  onSwitchToGuide?: (lastQuestion: string) => void;
}

export const useBhanzuBuddyChat = ({
  leadId,
  source = API_CONSTANTS.DEFAULT_SOURCE,
  onMessageSent,
  onError,
  onSwitchToGuide,
}: UseBhanzuBuddyChatProps) => {
  const [messages, setMessages] = React.useState<BhanzuBuddyChatMessage[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = React.useState(false);
  const [currentSessionUuid] = React.useState<string>('');
  const { selectedVariantId } = useProfilesData();
  const queryClient = useQueryClient();

  // Unified message-sent handler: appends buddy answer and removes loading placeholders
  const handleChatResponseAppend = React.useCallback(
    (data: ChatResponse) => {
      if (data && typeof data === 'object' && 'answer' in data) {
        const maybeAnswer = (data as { answer: unknown }).answer;
        if (typeof maybeAnswer === 'string') {
          const buddyMessage: BhanzuBuddyChatMessage = {
            id: `buddy-${Date.now()}`,
            type: 'buddy',
            content: maybeAnswer,
            timestamp: new Date(),
          };
          setMessages(prev => {
            const withoutLoading = prev.filter(msg => !msg.isLoading);
            return [...withoutLoading, buddyMessage];
          });
        }
      }
      onMessageSent?.(data);
    },
    [onMessageSent]
  );

  // Use extracted API interactions hook
  const {
    chatHistoryQuery,
    sendMessageMutation,
    guideMutation,
    sendFeedbackMutation,
    handleFeedback: handleFeedbackBase,
  } = useApiInteractions({
    leadId,
    source,
    onMessageSent: handleChatResponseAppend,
    onError,
  });

  // When interaction-history returns, replace messages with seeded history
  React.useEffect(() => {
    const interactions = chatHistoryQuery.data?.interactions;
    if (!Array.isArray(interactions) || interactions.length === 0) return;
    const seeded = interactions.flatMap(interaction =>
      transformInteractionToMessages(interaction)
    );
    setMessages(seeded);
  }, [chatHistoryQuery.data?.interactions]);

  // Use extracted guide flow hook
  const { handleGuideFlow } = useGuideFlow({
    leadId,
    selectedVariantId,
    currentSessionUuid,
    guideMutation,
    onMessageSent: handleChatResponseAppend,
    onError,
  });

  // Wrap guide flow trigger to also show the user's question and a loading placeholder
  const handleGuideFlowWithUI = React.useCallback(
    (question: string) => {
      // Add user message immediately
      const userMessage: BhanzuBuddyChatMessage = {
        id: `user-${Date.now()}`,
        type: 'user',
        content: question,
        timestamp: new Date(),
      };
      // Add loading message
      const loadingMessage: BhanzuBuddyChatMessage = {
        id: `loading-${Date.now()}`,
        type: 'buddy',
        content: '',
        timestamp: new Date(),
        isLoading: true,
      };
      setMessages(prev => [...prev, userMessage, loadingMessage]);

      // Continue with the guide flow sequence (history -> answer)
      Promise.resolve()
        .then(() => handleGuideFlow(question))
        .catch(() => {});
    },
    [handleGuideFlow]
  );

  // Memoize send message error callback
  const handleSendMessageError = React.useCallback(
    (error: unknown) => {
      // Add a polished BhanzuBuddy error response to the chat
      const errorMessage = getErrorMessage(error);
      const buddyErrorMessage: BhanzuBuddyChatMessage = {
        id: `buddy-error-${Date.now()}`,
        type: 'buddy',
        content: errorMessage,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, buddyErrorMessage]);
      onError?.(error);
    },
    [onError]
  );

  // Memoize loading message removal callbacks
  const handleMutationSuccess = React.useCallback(() => {
    // Remove loading message
    setMessages(prev => prev.filter(msg => !msg.isLoading));
  }, []);

  const handleMutationError = React.useCallback(() => {
    // Remove loading message on error
    setMessages(prev => prev.filter(msg => !msg.isLoading));
  }, []);

  const handleSendMessage = React.useCallback(
    (
      message: string,
      overrideSource?: string,
      extra?: { question_by_bb?: string; answer_by_student?: string }
    ) => {
      const messageSource = overrideSource ?? source;
      // Add user message immediately
      const userMessage: BhanzuBuddyChatMessage = {
        id: `user-${Date.now()}`,
        type: 'user',
        content: message,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, userMessage]);

      // Add loading message
      const loadingMessage: BhanzuBuddyChatMessage = {
        id: `loading-${Date.now()}`,
        type: 'buddy',
        content: '',
        timestamp: new Date(),
        isLoading: true,
      };

      setMessages(prev => [...prev, loadingMessage]);

      // Send to API
      const chatData: ChatMessage = {
        question: message,
        lead_id: leadId,
        source: messageSource,
        variant_id: selectedVariantId ?? '',
        session_uuid: currentSessionUuid || undefined,
        ...(extra?.question_by_bb && { question_by_bb: extra.question_by_bb }),
        ...(extra?.answer_by_student && {
          answer_by_student: extra.answer_by_student,
        }),
      };

      sendMessageMutation.mutate(chatData, {
        onSuccess: handleMutationSuccess,
        onError: error => {
          handleMutationError();
          handleSendMessageError(error);
        },
      });
    },
    [
      leadId,
      source,
      currentSessionUuid,
      sendMessageMutation,
      selectedVariantId,
      handleMutationSuccess,
      handleMutationError,
      handleSendMessageError,
    ]
  );

  // Send message with Quick Challenge payload enrichment
  const handleSendQuickChallengeAnswer = React.useCallback(
    (answerByStudent: string, questionByBB: string) => {
      // For QC, the "question" field should contain the user's answer
      // and question_by_bb should contain the original QC question
      handleSendMessage(answerByStudent, undefined, {
        question_by_bb: questionByBB,
        answer_by_student: answerByStudent,
      });
    },
    [handleSendMessage]
  );

  // Utility to append a buddy message (used to show QC question as bot)
  const appendBuddyMessage = React.useCallback((content: string) => {
    const buddyMessage: BhanzuBuddyChatMessage = {
      id: `buddy-${Date.now()}`,
      type: 'buddy',
      content,
      timestamp: new Date(),
      isQuickChallenge: true, // Mark as QC message to hide feedback/guide
    };
    setMessages(prev => [...prev, buddyMessage]);
  }, []);

  const handleFeedback = React.useCallback(
    (messageId: string, type: 'positive' | 'negative') => {
      handleFeedbackBase(messageId, type, messages);
    },
    [handleFeedbackBase, messages]
  );

  const handleGuideMe = React.useCallback(() => {
    // Find the last user question
    const userMessages = messages.filter(msg => msg.type === 'user');
    const lastUserMessage = userMessages.at(-1);

    if (lastUserMessage && onSwitchToGuide) {
      onSwitchToGuide(lastUserMessage.content);
    }
  }, [messages, onSwitchToGuide]);

  // Manual refetch function that accepts a new source parameter
  const refetchWithSource = React.useCallback(
    (newSource: string) => {
      // Imperatively fetch interaction history for new source
      const queryKey = [
        'bhanzu-buddy',
        'chat-history',
        'get',
        { lead_id: leadId, source: newSource },
      ] as const;
      // show a transient history-loading state to the UI via messages
      setMessages(prev => {
        // keep user messages; remove previous seeded history to avoid mix state
        const preserved = prev.filter(m => m.type === 'user');
        return preserved;
      });
      setIsHistoryLoading(true);
      void queryClient
        .fetchQuery({
          queryKey,
          queryFn: () => getChatHistory({ lead_id: leadId, source: newSource }),
        })
        .finally(() => setIsHistoryLoading(false));
    },
    [leadId, queryClient]
  );

  return {
    messages,
    isLoading:
      chatHistoryQuery.isLoading ||
      sendMessageMutation.isPending ||
      sendFeedbackMutation.isPending ||
      guideMutation.isPending,
    isHistoryLoading,
    isTyping: sendMessageMutation.isPending,
    onSendMessage: handleSendMessage,
    onSendQuickChallengeAnswer: handleSendQuickChallengeAnswer,
    appendBuddyMessage,
    onFeedback: handleFeedback,
    onGuideMe: handleGuideMe,
    refetch: chatHistoryQuery.refetch,
    refetchWithSource,
    handleGuideFlow: handleGuideFlowWithUI,
  };
};
